import { useEffect, useState } from 'react';
import { Box, Autocomplete, TextField, CircularProgress } from '@mui/material';
import { useDataProvider, useNotify } from 'react-admin';
import { TPADataType } from '../../utils/DataTypes';
import { REPORT_TYPES } from '../../constants';

interface TpaMultiSelectFilterProps {
  selectedTpas: TPADataType[];
  setSelectedTpas: (tpas: TPADataType[]) => void;
  label?: string;
  disabled?: boolean;
  reportType?: string;
}

const TpaMultiSelectFilter = ({
  selectedTpas,
  setSelectedTpas,
  label = 'Select TPAs',
  disabled = false,
  reportType,
}: TpaMultiSelectFilterProps) => {
  const [tpaList, setTpaList] = useState<TPADataType[]>([]);
  const dataProvider = useDataProvider();
  const [loading, setLoading] = useState(false);
  const notify = useNotify();

  useEffect(() => {
    const resource =
      reportType === REPORT_TYPES.PA_STATUS_REPORT ? 'TPAsWithPAStatusEnabled' : 'TPAsWithSummaryConfigs';

    setLoading(true);
    dataProvider
      .getList(resource, {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'vendor_name', order: 'ASC' },
        filter: {},
      })
      .then(({ data }) => {
        setTpaList(data);
        setLoading(false);
      })
      .catch(() => {
        notify('Error: Could not fetch TPAs with configs', { type: 'error' });
        setLoading(false);
      });
  }, [dataProvider, notify, reportType]);

  return (
    <Box sx={{ width: '100%' }}>
      <Autocomplete
        multiple
        options={tpaList}
        getOptionLabel={(option) => option.vendor_name || ''}
        isOptionEqualToValue={(option, value) => option.vendor_number === value.vendor_number}
        value={selectedTpas}
        onChange={(event, newValue) => setSelectedTpas(newValue)}
        loading={loading}
        disabled={disabled}
        filterSelectedOptions
        disableCloseOnSelect
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            size="small"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Box>
  );
};

export default TpaMultiSelectFilter;
